import React, { memo, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Paper,
  Tooltip,
  Typography,
  Grid
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { Collector, CollectorSettingScope } from 'graphql/types';
import { CollectorInstallInfo } from './AllCollectorsTab';
import CollectorStatus from './CollectorStatus';
import { CollectorIconType } from './AllCollectorsListPage';

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: theme.spacing(28),
    borderRadius: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(2),
    width: theme.spacing(25),
    borderColor: 'rgba(0,0,0,0.12)',
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      boxShadow: '0 0 5px rgba(33,33,33,.3)'
    },
    paddingBottom: theme.spacing(1),
    cursor: 'pointer'
  },
  media: {
    borderRadius: theme.spacing(0),
    height: theme.spacing(15),
    display: 'flex',
    paddingLeft: theme.spacing(4.5),
    alignItems: 'center'
  },
  collectorIcon: {
    maxHeight: theme.spacing(10),
    flexGrow: 1
  },

  title: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    overflow: 'visible',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    position: 'relative'
  },
  connected: {
    padding: theme.spacing(0, 0.8),
    ...theme.typography.caption,
    color: theme.palette.success.light,
    backgroundColor: theme.palette.success.dark,
    marginLeft: theme.spacing(1.5),
    float: 'left',
    position: 'relative',
    bottom: theme.spacing(1.2),
    top: theme.spacing(-1.2),
    width: 'fit-content',
    borderRadius: theme.spacing(0.5)
  },
  cardContent: {
    padding: ({ isInstalled }: { isInstalled: number }) =>
      isInstalled > 0
        ? `${theme.spacing(0, 2, 0, 2)} !important`
        : `${theme.spacing(2.2, 2, 0, 2)} !important`,

    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(1),
    justifyContent: 'space-between'
  },

  category: {
    display: 'flex',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize
  },
  categoryBox: {
    position: 'relative',
    margin: theme.spacing(-2.75, 0, 1, 0)
  }
}));

const useTooltipClasses = makeStyles((theme) => ({
  popper: {
    marginBottom: `${theme.spacing(-13)} !important`
  }
}));

type CollectorCardComponentProps = {
  info: Collector;
  index: number;
  handleOpenDialog: (item: any) => void;
  settingObj: Record<string, CollectorInstallInfo>;
};

export const CollectorCardComponent: React.FC<CollectorCardComponentProps> = memo(
  ({
    info,
    index,
    handleOpenDialog,
    settingObj
  }: CollectorCardComponentProps) => {
    const classes = useStyles({
      isInstalled: info.installations?.items?.length || 0
    });
    const tooltip = useTooltipClasses();

    const getStatus = useCallback(
      (
        info: Collector
      ): {
        retUserStatus: string;
        retTenantStatus: string;
      } => {
        const collectorSettingDetails = settingObj[info?.id];

        const userStatus = collectorSettingDetails?.userSettings?.find(
          (s) => s?.key === 'syncStatus'
        )?.value;
        const tenantStatus = collectorSettingDetails?.tenantSettings?.find(
          (s) => s?.key === 'syncStatus'
        )?.value;
        if (userStatus || tenantStatus) {
          const retUserStatus = (userStatus || '').trim();
          const retTenantStatus = (tenantStatus || '')?.trim();
          if (retUserStatus.length > 0 || retTenantStatus?.length > 0)
            return { retUserStatus, retTenantStatus };
        }
        return {
          retUserStatus:
            (collectorSettingDetails?.userInstallStatus?.errors?.length || 0) >
            0
              ? 'Failed'
              : '',
          retTenantStatus:
            (collectorSettingDetails?.tenantInstallStatus?.errors?.length ||
              0) > 0
              ? 'Failed'
              : ''
        };
      },
      [settingObj]
    );

    const getInstallId = useCallback((item: Collector) => {
      let userInstallId = '';
      let installId = '';
      item.installations?.items?.forEach((i) => {
        if (i?.scope === CollectorSettingScope.User)
          userInstallId = i?.installId;
        else installId = i?.installId || '';
      });
      return { userInstallId, installId };
    }, []);

    const getInstallStatus = useCallback(
      (item: Collector) => {
        const { userInstallId, installId } = getInstallId(item);
        const { retUserStatus, retTenantStatus } = getStatus(item);

        if ((item?.installations?.items?.length || 0) > 0)
          return (
            <div>
              <span
                className={classes.connected}
                data-testid={'connected-button-' + item.name}
              >
                <FormattedMessage id="collectors.installed" />
              </span>
              {(retTenantStatus || retUserStatus) &&
                (item?.installations?.items?.length || 0) && (
                  <CollectorStatus
                    onClick={() => handleOpenDialog(item)}
                    syncStatus={retTenantStatus}
                    installId={installId}
                    userSyncStatus={retUserStatus}
                    userInstallId={userInstallId}
                  />
                )}
            </div>
          );
      },
      [classes.connected, getInstallId, getStatus, handleOpenDialog]
    );

    return (
      <Grid item key={info.id} data-testid={`collectoritem${info.name}`}>
        <Card
          className={classes.card}
          elevation={0}
          variant="outlined"
          key={info.id}
          onClick={() => handleOpenDialog(info)}
          data-testid={`collectorcard${info.name}`}
        >
          <CardMedia>
            <Paper className={classes.media} elevation={0}>
              <img
                className={classes.collectorIcon}
                src={info.image.src}
                data-testid={`collecetorImage${index}`}
              />
              <CollectorIconType type={info.type} />
            </Paper>
          </CardMedia>
          {getInstallStatus(info)}
          <CardContent className={classes.cardContent}>
            <Tooltip
              title={info.name}
              data-testid={`collector-title-tooltip${index}`}
              placement="top-start"
              classes={tooltip}
            >
              <Typography
                className={classes.title}
                variant="body1"
                data-testid={`collectorName${index}`}
              >
                {info.name}
              </Typography>
            </Tooltip>
            <div
              data-testid={`collector-category-content${index}`}
              className={classes.categoryBox}
            >
              <Typography className={classes.category}>
                {info.category}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    );
  }
);
CollectorCardComponent.displayName = 'CollectorCardComponent';
